import * as THREE from 'three';
import vertexShader from '../shaders/ripple.vert?raw';
import fragmentShader from '../shaders/ripple.frag?raw';

const MAX_RIPPLES = 5;

interface ActiveRipple {
  center: THREE.Vector2;
  startTime: number;
}

export class RipplePlane {
  private mesh: THREE.Mesh;
  private canvasWidth: number;
  private canvasHeight: number;
  private imageAspectRatio: number = 1.0;
  private textureLoaded: boolean = false;

  private imagePath: string = '';
  private ripples: ActiveRipple[] = [];
  private readonly rippleDuration: number = 2.2;
  private readonly uniformRippleCenters: THREE.Vector2[] = Array.from({ length: MAX_RIPPLES }, () => new THREE.Vector2(0.5, 0.5));
  private readonly uniformRippleTimes: number[] = new Array<number>(MAX_RIPPLES).fill(-1.0);
  private readonly uniformRippleActives: number[] = new Array<number>(MAX_RIPPLES).fill(0.0);

  constructor(width: number, height: number, imagePath: string) {
    this.imagePath = imagePath;
    this.canvasWidth = width;
    this.canvasHeight = height;
    this.mesh = this.createMesh(width, height);

  }

  // ------------------------ Public ------------------------
  public getInstance = (): THREE.Mesh => {
    return this.mesh;
  }

  public triggerRipple = (uv: THREE.Vector2): void => {
    const now = performance.now() / 1000.0;
    this.ripples.push({
      center: uv.clone(),
      startTime: now,
    });
    if (this.ripples.length > MAX_RIPPLES) {
      this.ripples = this.ripples.slice(this.ripples.length - MAX_RIPPLES);
    }
  }

  public updateTexture = (imagePath: string): void => {
    if (!imagePath || imagePath === this.imagePath) return;
    const material = this.mesh.material as THREE.ShaderMaterial;
    const currentTexture = material.uniforms.uTexture.value as THREE.Texture | undefined;
    this.imagePath = imagePath;
    this.textureLoaded = false;
    const newTexture = this.loadTexture(imagePath);
    material.uniforms.uTexture.value = newTexture;
    material.needsUpdate = true;
    if (currentTexture) currentTexture.dispose();
    this.updateGeometryForWidthFit();
  }

  public update = (_elapsedTimeMs: number): void => {
    const currentTime = performance.now() / 1000.0;

    this.ripples = this.ripples.filter((ripple) => (currentTime - ripple.startTime) <= this.rippleDuration);

    const centers = this.uniformRippleCenters;
    const times = this.uniformRippleTimes;
    const actives = this.uniformRippleActives;

    for (let i = 0; i < MAX_RIPPLES; i++) {
      const ripple = this.ripples[i];
      if (ripple) {
        const elapsed = currentTime - ripple.startTime;
        centers[i].copy(ripple.center);
        times[i] = elapsed;
        actives[i] = 1.0;
      } else {
        centers[i].set(0.5, 0.5);
        times[i] = -1.0;
        actives[i] = 0.0;
      }
    }

    const material = this.mesh.material as THREE.ShaderMaterial;
    material.uniforms.uRippleCenters.value = centers;
    material.uniforms.uRippleTimes.value = times;
    material.uniforms.uRippleActives.value = actives;
  }

  // ------------------------ Private ------------------------
  private createMaterial = (width: number, height: number): THREE.ShaderMaterial => {
    const texture = this.loadTexture(this.imagePath);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTexture: { value: texture },
        uImageAspect: { value: this.imageAspectRatio },
        uRippleCenters: { value: this.uniformRippleCenters },
        uRippleTimes: { value: this.uniformRippleTimes },
        uRippleActives: { value: this.uniformRippleActives },
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: false,
    });
    return material;
  }

  private createMesh = (width: number, height: number): THREE.Mesh => {
    const geometry = new THREE.PlaneGeometry(width, height, 1, 1);
    const material = this.createMaterial(width, height);
    const mesh = new THREE.Mesh(geometry, material);
    return mesh;
  }

  public resize = (width: number, height: number): void => {
    this.canvasWidth = width;
    this.canvasHeight = height;
    // 画像比に合わせてジオメトリを幅フィットへ更新し、上端固定で配置
    this.updateGeometryForWidthFit();
  }

  private updateGeometryForWidthFit = (): void => {
    if (!this.mesh) return;
    const planeWidth = this.canvasWidth;
    const planeHeight = (this.textureLoaded && this.imageAspectRatio > 0.0)
      ? this.canvasWidth / this.imageAspectRatio
      : this.canvasHeight;

    // Dispose old geometry to avoid memory leaks
    this.mesh.geometry.dispose();
    this.mesh.geometry = new THREE.PlaneGeometry(planeWidth, planeHeight, 1, 1);

    // 上端固定で配置（Plane上端 = カメラ上端）
    this.updateTopAnchorPosition(planeHeight);
  }

  private updateTopAnchorPosition = (planeHeight: number): void => {
    const centerY = (this.canvasHeight - planeHeight) * 0.5;
    this.mesh.position.y = centerY;
  }

  private loadTexture = (imagePath: string): THREE.Texture => {
    this.textureLoaded = false;
    if (!imagePath) {
      return new THREE.Texture();
    }
    const textureLoader = new THREE.TextureLoader();
    const texture = textureLoader.load(imagePath);
    texture.onUpdate = () => {
      if (texture.image) {
        this.imageAspectRatio = texture.image.width / texture.image.height;
        this.textureLoaded = true;
        if (this.mesh && this.mesh.material) {
          const material = this.mesh.material as THREE.ShaderMaterial;
          material.uniforms.uImageAspect.value = this.imageAspectRatio;
        }
        this.updateGeometryForWidthFit();
      }
    };
    return texture;
  }
}
